/**
 *  Copyright (c) 2022-present TVM Engineering Authors.
 *
 */
/* eslint-disable */
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Visibility, VisibilityOff, PersonOutline, LockOutlined } from "@mui/icons-material";
import {
  TextField,
  Paper,
  Link,
  FormControl,
  InputAdornment,
  FormHelperText,
  IconButton,
  Typography,
  Button,
  Divider,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import LoadingScreen from "../../feedback/LoadingScreen";
import STATIC_ROUTES from "../../../route/STATIC_ROUTES";

import PopperPopupState from "../../common/PopperPopupState";
import i18n from "../../../services/i18n";
import { MIN_PASS_LENGTH, USER_LENGTH } from "../../../config";
import { fetchSignIn, verifyQrCode } from "../../../services/auth";
import { getErrorMessage } from "../../../reducers/auth";
import Authenticators from "./Authenticators";
import { getMaintanenceStatus } from "../../../services/serverNotifications";
import ResetPasswordDialog from "../../dialogs/ResetPassword/ResetPasswordDialog";
// Use this to override simple styles

const styles = {
  formContainer: {
    width: "300px",
    padding: "24px 40px 48px 40px",
    borderRadius: "12px",
    fontFamily: "IBM Plex Sans",
  },
  forgotYourText: {
    textAlign: "center",
    color: "#505257",
    fontSize: "1rem",
  },
  linkText: {
    color: "#000000",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  confirmButton: {
    width: "100%",
    borderRadius: "2px",
    fontSize: "14px",
    fontFamily: "IBM Plex Sans",
    marginTop: "25px",
    backgroundColor: "#1E9DF2",
    height: "50px",
  },
};

// Use this aprouch to override more complex styles on a component

const theme = createTheme({
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          marginTop: "30px",
          "&:before": {
            background: "transparent",
            top: "20px",
            right: "12%",
            width: "25px",
            height: "25px",
            margin: "0 auto",
            content: "''",
            display: "inline-block",
            position: "absolute",
            transform: "rotate(45deg)",
            background: "#fff",
            boxShadow:
              "-3px -3px 3px -3px rgb(0 0 0 / 0%), -3px -3px 3px -3px rgb(0 0 0 / 2%), -3px -3px 3px -2px rgb(0 0 0 / 10%)",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          height: "0.5rem",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "0px",
        },
      },
    },
  },
});

interface Props {
  open?: boolean;
  onClose: () => void;
  anchorEl?: Object | null;
}

const LoginMenu = React.memo((props: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname, search } = history.location;

  const [state, setState] = useState({
    username: "",
    password: "",
    remember: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errorUsername, setErrorUsername] = useState(false);
  const [errorPassword, setErrorPassword] = useState(false);
  const [userAuth, setUserAuth] = useState([]);
  const [verifyCode, setVerifyCode] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [verifyState, setVerifyState] = useState("");
  const [serverStatus, setServerStatus] = useState(true);
  const [isResetPasswordFormOpen, setIsResetPasswordFormOpen] = useState(false);

  const { errorMessage } = useSelector((globalState) => ({
    errorMessage: getErrorMessage(globalState),
  }));

  const handleValidation = useCallback(() => {
    if (state.username.length > USER_LENGTH) {
      setErrorUsername(false);
      // setDisableConfirmButton(false);
    }

    if (errorMessage) {
      setErrorUsername(true);
      // setDisableConfirmButton(true);
    }

    if (state.password.length > MIN_PASS_LENGTH) {
      setErrorPassword(false);
      // setDisableConfirmButton(false);
    }

    if (errorMessage) {
      setErrorPassword(true);
      // setDisableConfirmButton(true);
    }

    if (errorUsername || errorPassword) {
      // setDisableConfirmButton(true);
    } else {
      // setDisableConfirmButton(false);
    }
  }, [state, errorMessage, errorUsername, errorPassword]);

  useEffect(() => {
    handleValidation();
  }, [handleValidation]);

  function handleInputChange(event) {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;

    setState({
      ...state,
      [name]: value,
    });
  }

  function keyPress(e) {
    if (e.key === "Enter") {
      onConfirm();
    }
  }

  async function getMaintanenceServerStatus() {
    await getMaintanenceStatus()
      .then((data) => setServerStatus(data.maintanence[0].active))
      .catch((error) => {
        console.log(error);
      });
  }

  useEffect(() => {
    getMaintanenceServerStatus();
  }, []);

  async function onConfirm() {
    const urlToRedirect = pathname + search;

    setIsLoading(true);

    const userdata = dispatch(
      fetchSignIn(
        {
          user: state.username,
          pass: state.password,
        },
        serverStatus,
        urlToRedirect
      )
    );

    if (userdata) {
      userdata
        .then((result) => result)
        .then((data) => setUserAuth(data))
        .catch((err) => console.log(err));
      setIsLoading(false);
    }
  }

  function keyPressVarification(e) {
    if (e.key === "Enter") {
      handleVerifyCode(verifyCode);
    }
  }

  function handleVerifyCode(code) {
    const urlToRedirect = pathname + search;
    const data = { id: userAuth?.id, token: userAuth?.token, code };
    dispatch(verifyQrCode(data, urlToRedirect));
  }

  function handleMouseDownPassword(event) {
    event.preventDefault();
  }

  function handleClickShowPassword() {
    setShowPassword(!showPassword);
  }

  useEffect(() => {
    setUserAuth(userAuth);
  }, [userAuth]);

  // Use this for 2FA

  // return (
  //   <ThemeProvider theme={theme}>
  //     <PopperPopupState
  //       {...props}
  //       content={
  //         // eslint-disable-next-line
  //         isLoading ? (
  //           <Paper elevation={3}>
  //             <Typography component="div" sx={styles.formContainer}>
  //               <LoadingScreen />
  //             </Typography>
  //           </Paper>
  //         ) : userAuth?.user ? (
  //           <Authenticators
  //             user={userAuth}
  //             verifyState={verifyState}
  //             setVerifyState={setVerifyState}
  //           />
  //         ) : (
  //           <Paper elevation={3}>
  //             <Typography component="div" sx={styles.formContainer}>
  //               <FormControl
  //                 fullWidth={true}
  //                 error={errorUsername}
  //                 // sx={styles.signInText}
  //                 // sx={{fontSize: '5rem'}}
  //               >
  //                 <TextField
  //                   id="signInUsername"
  //                   name="username"
  //                   label={i18n.t("core:username") + " or " + i18n.t("core:email")}
  //                   onChange={handleInputChange}
  //                   sx={styles.textField}
  //                   margin="dense"
  //                   variant="outlined"
  //                   fullWidth
  //                   InputProps={{
  //                     startAdornment: (
  //                       <InputAdornment position="start">
  //                         <PersonOutline />
  //                       </InputAdornment>
  //                     ),
  //                   }}
  //                   onKeyPress={keyPress}
  //                   autoFocus
  //                 />
  //                 {errorUsername && (
  //                   <FormHelperText>{i18n.t("core:invalidUsername")}</FormHelperText>
  //                 )}
  //               </FormControl>
  //               <FormControl
  //                 fullWidth={true}
  //                 error={errorPassword}
  //                 // sx={styles.signInText}
  //               >
  //                 <TextField
  //                   id="signInPassword"
  //                   type={showPassword ? "text" : "password"}
  //                   name="password"
  //                   label={i18n.t("core:password")}
  //                   onChange={handleInputChange}
  //                   sx={styles.textField}
  //                   margin="dense"
  //                   variant="outlined"
  //                   fullWidth
  //                   InputProps={{
  //                     startAdornment: (
  //                       <InputAdornment position="start">
  //                         <LockOutlined />
  //                       </InputAdornment>
  //                     ),
  //                     endAdornment: (
  //                       <InputAdornment position="end">
  //                         <IconButton
  //                           aria-label="toggle password visibility"
  //                           onClick={handleClickShowPassword}
  //                           onMouseDown={handleMouseDownPassword}
  //                         >
  //                           {showPassword ? <VisibilityOff /> : <Visibility />}
  //                         </IconButton>
  //                       </InputAdornment>
  //                     ),
  //                   }}
  //                   onKeyPress={keyPress}
  //                 />
  //                 {errorPassword && (
  //                   <FormHelperText>{i18n.t("core:yourPasswordIsInvalid")}</FormHelperText>
  //                 )}
  //               </FormControl>

  //               <Typography component="div">
  //                 <Button
  //                   id="signInConfirm"
  //                   variant="contained"
  //                   color="primary"
  //                   sx={styles.confirmButton}
  //                   onClick={onConfirm}
  //                 >
  //                   {i18n.t("core:userLogin")}
  //                 </Button>
  //               </Typography>

  //               <Typography component="p" sx={styles.forgotYourText}>
  //                 {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
  //                 <Link
  //                   sx={styles.linkText}
  //                   component="button"
  //                   variant="body2"
  //                   onClick={() => {
  //                     history.push(STATIC_ROUTES.forgotPassword);
  //                     history.go(STATIC_ROUTES.forgotPassword);
  //                   }}
  //                 >
  //                   {i18n.t("core:ForgotYourPassword")}
  //                 </Link>
  //               </Typography>
  //             </Typography>
  //           </Paper>
  //         )
  //       }
  //     />
  //   </ThemeProvider>
  // );
  return (
    <ThemeProvider theme={theme}>
      <PopperPopupState
        {...props}
        content={
          <Paper
            elevation={3}
            sx={{
              borderRadius: "12px",
              cursor: "default",
              "& fieldset": { background: "transparent" },
            }}
          >
            <Typography component="div" sx={styles.formContainer}>
              <p
                style={{
                  fontSize: "36px",
                  fontStyle: "normal",
                  fontWeight: "600",
                  lineHeight: "150%",
                  letterSpacing: "-0.72px",
                  margin: "0px 0px 25px 0px",
                }}
              >
                Login
              </p>
              <FormControl
                fullWidth={true}
                error={errorUsername}
                // sx={styles.signInText}
                // sx={{fontSize: '5rem'}}
              >
                <TextField
                  id="signInUsername"
                  name="username"
                  label={i18n.t("core:username") + " or " + i18n.t("core:email")}
                  onChange={handleInputChange}
                  sx={{
                    "& .MuiInputBase-root": {
                      borderRadius: "4px",
                      border: " 0px solid var(--blc-60, #D1D2D2)",
                    },
                  }}
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutline />
                      </InputAdornment>
                    ),
                  }}
                  onKeyPress={keyPress}
                  autoFocus
                />
                {errorUsername && <FormHelperText>{i18n.t("core:invalidUsername")}</FormHelperText>}
              </FormControl>
              <FormControl
                fullWidth={true}
                error={errorPassword}
                // sx={styles.signInText}
                sx={{ paddingTop: "25px" }}
              >
                <TextField
                  id="signInPassword"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  label={i18n.t("core:password")}
                  onChange={handleInputChange}
                  sx={{
                    "& .MuiInputBase-root": {
                      borderRadius: "4px",
                      border: " 0px solid var(--blc-60, #D1D2D2)",
                    },
                  }}
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  onKeyPress={keyPress}
                />
                {errorPassword && (
                  <FormHelperText>{i18n.t("core:yourPasswordIsInvalid")}</FormHelperText>
                )}
                <p
                  // onClick={() => {
                  //   history.push(STATIC_ROUTES.forgotPassword);
                  //   history.go(STATIC_ROUTES.forgotPassword);
                  // }}
                  onClick={() => setIsResetPasswordFormOpen(true)}
                  style={{
                    color: "#1E9DF2",
                    fontSize: "12px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "16px",
                    margin: "0px 16px",
                    cursor: "pointer",
                  }}
                >
                  Forgot password?
                </p>
              </FormControl>
              <ResetPasswordDialog
                open={isResetPasswordFormOpen}
                setOpen={setIsResetPasswordFormOpen}
              />
              <Typography component="div">
                <Button
                  id="signInConfirm"
                  variant="contained"
                  color="primary"
                  sx={styles.confirmButton}
                  onClick={onConfirm}
                >
                  {i18n.t("core:userLogin")}
                </Button>
              </Typography>
            </Typography>
          </Paper>
        }
      />
    </ThemeProvider>
  );
});

export default LoginMenu;
